import { compileSchema } from '@toolproof-core/validation/_pure/schema-compilation';

import { parseDirectionCatalog, selectDirectionRuleEffects } from '@/_pure/directionRule';
import effectSchema from '@/adapters/primitive/_contract/_@schema/directions/cameraEffect.schema.json' with { type: 'json' };
import cameraRulesJson from '@/adapters/primitive/_data/directions/cameraRules.json' with { type: 'json' };
import { PRIMITIVE_MOMENT_VOCABULARY } from './primitiveMoment';
import type { Moment, MomentVocabulary } from '@/_contract/_~generated/directionMomentSchema';
import type { DirectionCatalog } from '@/_contract/directionRule';
import type { PrimitiveCameraDirectionEffect } from '@/adapters/primitive/_contract/_~generated/cameraDirectionEffect';

export function parsePrimitiveCameraCatalog(
    value: unknown, vocabulary: MomentVocabulary = PRIMITIVE_MOMENT_VOCABULARY,
): DirectionCatalog<PrimitiveCameraDirectionEffect> {
    const validator = compileSchema(effectSchema);
    return parseDirectionCatalog(value, vocabulary, effect => {
        const result = validator.validate(effect);
        if (!result.valid) {
            throw new Error(`Invalid primitive camera effect: ${result.issues.map(issue => `${issue.instancePath} ${issue.message}`).join('; ')}`);
        }
        return effect as PrimitiveCameraDirectionEffect;
    });
}

export const PRIMITIVE_CAMERA_CATALOG = parsePrimitiveCameraCatalog(cameraRulesJson);

/** Cue eligibility and anchors are facts; the selected rules decide presentation. */
export function selectPrimitiveCameraDirections(
    moment: Moment,
    catalog: DirectionCatalog<PrimitiveCameraDirectionEffect> = PRIMITIVE_CAMERA_CATALOG,
    isIncluded: (key: string) => boolean = () => false,
): ReadonlyMap<PrimitiveCameraDirectionEffect['cue'], PrimitiveCameraDirectionEffect> {
    const directions = new Map<PrimitiveCameraDirectionEffect['cue'], PrimitiveCameraDirectionEffect>();
    for (const effect of selectDirectionRuleEffects(catalog, { moment }, isIncluded)) {
        if (directions.has(effect.cue)) {
            throw new Error(`Multiple primitive camera rules select cue '${effect.cue}' at ${JSON.stringify(moment)}.`);
        }
        directions.set(effect.cue, effect);
    }
    return directions;
}
